import { createContext, useContext, useEffect } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import { ChevronDown, Globe2, Instagram, Mail } from "lucide-react"
import { publicContent } from "../content/publicContent"
import "../pages/HomePage.css"
import "./PublicLayout.css"

export const publicLanguages = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "tr", label: "Türkçe" },
  { code: "az", label: "Azərbaycanca" },
]

const PublicLocaleContext = createContext(null)

export const usePublicLocale = () => {
  const context = useContext(PublicLocaleContext)
  if (!context) throw new Error("Public pages must be rendered inside PublicLayout")
  return context
}

export const PublicLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const requestedLanguage = new URLSearchParams(location.search).get("lang")
  const language = publicLanguages.some(({ code }) => code === requestedLanguage) ? requestedLanguage : "tr"
  const copy = publicContent[language]
  const pageTitle = {
    "/": "whatsin?",
    "/support": `${copy.common.support} | whatsin?`,
    "/legal": `${copy.common.legal} | whatsin?`,
    "/legal/privacy-policy": `${copy.common.privacy} | whatsin?`,
  }[location.pathname]

  useEffect(() => {
    const previousLanguage = document.documentElement.lang
    const previousTitle = document.title
    document.documentElement.lang = language
    document.title = pageTitle
    return () => {
      document.documentElement.lang = previousLanguage
      document.title = previousTitle
    }
  }, [language, pageTitle])

  const localize = (path) => {
    const url = new URL(path, window.location.origin)
    url.searchParams.set("lang", language)
    return `${url.pathname}${url.search}${url.hash}`
  }

  const changeLanguage = (nextLanguage) => {
    const search = new URLSearchParams(location.search)
    search.set("lang", nextLanguage)
    navigate({ pathname: location.pathname, search: `?${search}`, hash: location.hash })
  }

  return (
    <PublicLocaleContext.Provider value={{ language, copy, localize, changeLanguage }}>
      <div className="public-site">
        <Outlet />
      </div>
    </PublicLocaleContext.Provider>
  )
}

export const LanguageSelector = () => {
  const { language, copy, changeLanguage } = usePublicLocale()
  return (
    <label className="public-language">
      <Globe2 size={18} aria-hidden="true" />
      <span className="public-visually-hidden">{copy.common.language}</span>
      <select value={language} onChange={(event) => changeLanguage(event.target.value)}>
        {publicLanguages.map(({ code, label }) => <option key={code} value={code} lang={code}>{label}</option>)}
      </select>
      <ChevronDown size={16} aria-hidden="true" className="public-language-chevron" />
    </label>
  )
}

export const PublicHeader = ({ home = false }) => {
  const { copy: { common }, localize } = usePublicLocale()
  return (
    <header className="container public-header">
      <Link to={localize("/")} className="public-brand">
        <img src="/assets/icon.png" alt={common.logoAlt} width="48" height="48" />
        <span>whatsin?</span>
      </Link>
      <nav className="public-navigation" aria-label={common.home}>
        {home ? <>
          <a href="#features">{common.features}</a>
          <a href="#how-it-works">{common.how}</a>
        </> : <Link to={localize("/")}>{common.home}</Link>}
        <LanguageSelector />
      </nav>
    </header>
  )
}

export const PublicFooter = () => {
  const { copy: { common }, localize } = usePublicLocale()
  return (
    <footer className="bg-gray-900 text-white py-12 public-footer">
      <div className="container">
        <div className="public-footer-content">
          <div>
            <Link to={localize("/")} className="public-brand">
              <img src="/assets/icon.png" alt={common.logoAlt} width="40" height="40" />
              <span>whatsin?</span>
            </Link>
            <p className="text-gray-400">{common.tagline}</p>
          </div>
          <nav className="public-footer-links" aria-label={common.legal}>
            <a href="https://www.instagram.com/whatsin_app/" target="_blank" rel="noopener noreferrer">
              <Instagram size={18} aria-hidden="true" /> Instagram
            </a>
            <Link to={localize("/")}>{common.home}</Link>
            <Link to={localize("/support")}>{common.support}</Link>
            <Link to={localize("/legal")}>{common.legal}</Link>
            <Link to={localize("/legal/privacy-policy")}>{common.privacy}</Link>
          </nav>
        </div>
        <p className="public-copyright">&copy; {new Date().getFullYear()} whatsin? {common.rights}</p>
      </div>
    </footer>
  )
}

export const PublicContact = ({ title, description }) => {
  const { copy: { common } } = usePublicLocale()
  return (
    <section className="public-contact">
      <Mail size={40} aria-hidden="true" />
      <h2>{title}</h2>
      <p>{description}</p>
      <a href="mailto:destek@whatsin-app.com">{common.email}</a>
      <p><a href="mailto:destek@whatsin-app.com">destek@whatsin-app.com</a></p>
    </section>
  )
}
